
import React, { useMemo, useState } from 'react';
import { Plus, Receipt, BrickWall, Tag } from 'lucide-react';
import { Project, ExpenseCategory } from '../types';
import { formatCurrency } from '../utils/formatters';

interface ExpensesViewProps {
  project: Project;
  onNewExpense: () => void;
  onEditExpense: (id: string) => void;
}

export const ExpensesView: React.FC<ExpensesViewProps> = ({ project, onNewExpense, onEditExpense }) => {
  const [filters, setFilters] = useState({
    stageId: 'all',
    category: 'all',
    search: '',
    startDate: '',
    endDate: ''
  });

  const filteredExpenses = useMemo(() => {
    return project.expenses.filter(e => {
      const matchStage = filters.stageId === 'all' || e.stageId === filters.stageId;
      const matchCategory = filters.category === 'all' || e.category === filters.category;
      const matchSearch = e.description.toLowerCase().includes(filters.search.toLowerCase()) || e.supplier.toLowerCase().includes(filters.search.toLowerCase());
      const matchDateStart = !filters.startDate || new Date(e.date) >= new Date(filters.startDate);
      const matchDateEnd = !filters.endDate || new Date(e.date) <= new Date(filters.endDate);
      return matchStage && matchCategory && matchSearch && matchDateStart && matchDateEnd;
    });
  }, [project.expenses, filters]);

  return (
    <div className="space-y-6 animate-fadeIn pb-24">
       <div className="flex items-center justify-between">
          <h2 className="text-3xl font-black uppercase tracking-tighter">Extrato</h2>
          <button onClick={onNewExpense} className="bg-emerald-600 text-white p-3 rounded-xl shadow-lg hover:scale-105 transition-transform"><Plus size={20}/></button>
       </div>
       
       <div className="flex gap-2 overflow-x-auto pb-2 no-scrollbar">
          <button onClick={() => setFilters({...filters, stageId: 'all'})} className={`px-4 py-2 rounded-xl text-xs font-black uppercase tracking-widest whitespace-nowrap border ${filters.stageId === 'all' ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800'}`}>Todos</button>
          {project.stages.map(s => (
            <button key={s.id} onClick={() => setFilters({...filters, stageId: s.id})} className={`px-4 py-2 rounded-xl text-xs font-black uppercase tracking-widest whitespace-nowrap border ${filters.stageId === s.id ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800'}`}>{s.name}</button>
          ))}
       </div>

       <div className="space-y-3">
          {filteredExpenses.length === 0 ? (
             <div className="text-center py-20 opacity-50">
                <Receipt size={48} className="mx-auto mb-4" />
                <p className="font-bold">Nenhum lançamento encontrado.</p>
             </div>
          ) : (
            filteredExpenses.map(expense => (
              <div key={expense.id} onClick={() => onEditExpense(expense.id)} className={`bg-white dark:bg-slate-900 p-4 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-800 flex justify-between items-center cursor-pointer hover:border-emerald-500/50 transition-colors`}>
                 <div className="flex items-center gap-4">
                    <div className={`size-10 rounded-xl flex items-center justify-center ${expense.isPaid ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-50 text-amber-600'}`}>
                       {expense.category === ExpenseCategory.MATERIALS ? <BrickWall size={18} /> : <Tag size={18} />}
                    </div>
                    <div>
                       <p className="text-sm font-black text-slate-900 dark:text-white uppercase">{expense.description}</p>
                       <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{new Date(expense.date).toLocaleDateString('pt-BR')} • {expense.supplier}</p>
                    </div>
                 </div>
                 <div className="text-right">
                    <p className="text-sm font-black text-slate-900 dark:text-white">{formatCurrency(expense.amount)}</p>
                    {expense.installments > 1 && <span className="text-[8px] font-bold text-slate-400 uppercase tracking-widest bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded-md">Parc.</span>}
                 </div>
              </div>
            ))
          )}
       </div>
    </div>
  );
};
